"use client";

import Cookies from "js-cookie";

export type UserLocation = { state?: string; metro?: string } | null;

// Read `userLocation` FIRST. It is the canonical cookie every writer updates;
// `phoenix` is legacy and some write paths (LocationAndCategories, posts/create)
// only set `userLocation`, so `phoenix` can be stale. Reading it first (the old
// order) made search/posts bind a stale location even though the new one had
// saved. This order also matches the server read in app/page.tsx
// (`userLocation || phoenix`). writeLocationCookie() keeps both in sync going
// forward; preferring userLocation also un-sticks already-stale phoenix cookies.
const COOKIE_KEYS = ["userLocation", "phoenix"] as const;

export function readLocationCookie(): UserLocation {
  for (const key of COOKIE_KEYS) {
    try {
      const raw = Cookies.get(key);
      if (!raw) continue;
      // phoenix may be JSON or a simple slug; accept both
      try {
        const obj = JSON.parse(raw);
        const state = obj?.state ? String(obj.state).toUpperCase() : undefined;
        const metro = obj?.metro ? String(obj.metro).toLowerCase() : undefined;
        return { state, metro };
      } catch {
        const s = String(raw);
        if (s.includes("{")) continue;
        // If a plain slug is stored, treat as metro with unknown state
        return { state: undefined, metro: s.toLowerCase() };
      }
    } catch {}
  }
  return null;
}

export function writeLocationCookie(loc: { state?: string; metro?: string }) {
  const payload = JSON.stringify({
    state: loc.state ? String(loc.state).toUpperCase() : undefined,
    metro: loc.metro ? String(loc.metro).toLowerCase() : undefined,
  });
  try { Cookies.set("phoenix", payload, { expires: 30, path: "/" }); } catch {}
  try { Cookies.set("userLocation", payload, { expires: 30, path: "/" }); } catch {}
}

export function ensureLocationWithFallback(): UserLocation {
  const got = readLocationCookie();
  if (got) return got;
  // Geoselect fallback: try navigator.geolocation (permissionless rough check)
  // We cannot query without user consent; instead provide a neutral default.
  // Callers can open the LocationModal to refine.
  return { state: undefined, metro: undefined };
}

